<template>
  <AppLayout>
    <div class="edit-group-view">
      <div class="page-header">
        <button @click="goBack" class="back-button">
          <i class="icon-arrow-left"></i>
          Back to Group
        </button>
        <div class="header-content">
          <h1>Edit Group</h1>
          <p class="subtitle">Update your group settings and information</p>
        </div>
      </div>

      <div v-if="loadingGroup" class="loading-state">
        <div class="spinner-large"></div>
        <p>Loading group details...</p>
      </div>

      <div v-else-if="!currentGroup" class="error-state">
        <p>Group not found</p>
        <button @click="goBack" class="btn btn-secondary">Go Back</button>
      </div>

      <div v-else class="form-container">
        <GroupForm
          :group="currentGroup as any"
          :isLoading="isLoading"
          :serverError="error"
          @submit="handleSubmit"
          @cancel="cancel"
        />
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useGroupsStore } from '../../stores/groups';
import { storeToRefs } from 'pinia';
import AppLayout from '../../components/layout/AppLayout.vue';
import GroupForm from '../../components/groups/GroupForm.vue';
import type { UpdateGroupData } from '../../types/group';
import { useToast } from '../../composables/useToast';

const router = useRouter();
const route = useRoute();
const groupsStore = useGroupsStore();
const { isLoading, error, currentGroup } = storeToRefs(groupsStore);
const toast = useToast();

const groupId = ref(parseInt(route.params.id as string));
const loadingGroup = ref(false);

onMounted(async () => {
  if (!currentGroup.value || currentGroup.value.id !== groupId.value) {
    loadingGroup.value = true;
    try {
      await groupsStore.fetchGroup(groupId.value);
    } finally {
      loadingGroup.value = false;
    }
  }
});

const handleSubmit = async (data: UpdateGroupData) => {
  try {
    groupsStore.clearError();
    await groupsStore.updateGroup(groupId.value, data);
    toast.success('Group updated!', 'The group has been successfully updated.');
    router.push(`/groups/${groupId.value}`);
  } catch (err) {
    console.error('Failed to update group:', err);
    toast.error('Update failed', 'Failed to update the group. Please try again.');
  }
};

const cancel = () => {
  router.push(`/groups/${groupId.value}`);
};

const goBack = () => {
  router.push(`/groups/${groupId.value}`);
};
</script>

<style scoped>
.edit-group-view {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.2s ease;
  margin-bottom: 1rem;
}

.back-button:hover {
  color: #3b82f6;
}

.icon-arrow-left::before {
  content: '←';
  font-size: 1.25rem;
}

.header-content h1 {
  margin: 0;
  color: #1f2937;
  font-size: 2rem;
}

.subtitle {
  margin: 0.5rem 0 0 0;
  color: #6b7280;
  font-size: 1rem;
}

.form-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state,
.error-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.spinner-large {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
  transform: translateY(-1px);
}
</style>
